import { cn } from '@v2/shared/lib/utils';
import Stars from './Stars';
import { ProductSummary } from './types';

type Props = {
  summary: ProductSummary;
};

/**
 * What subscribers think of the magazines.
 *
 * Kept visually and structurally apart from the seller rating card, and never averaged
 * with it. A 4.6 across fifty thousand reviews of magazines says nothing about how well
 * we ship them, and presenting it as the company's rating would be a false claim about
 * the business — which is also why no aggregateRating for these reaches the page's
 * structured data.
 */
export default function ProductSummaryBand({ summary }: Props) {
  if (summary.reviewCount < 1) {
    return null;
  }

  return (
    <section
      aria-labelledby="product-rating-summary"
      className={cn(
        'mb-4 flex flex-wrap items-center gap-7 rounded-xl border border-solid border-slate-200',
        'bg-white px-7 py-5 shadow-sm'
      )}
    >
      <h2 id="product-rating-summary" className="sr-only">
        Magazine rating summary
      </h2>

      <div>
        <div className="text-4xl font-extrabold leading-none tracking-tight text-slate-900">
          {summary.averageRating.toFixed(1)}
        </div>
        <Stars rating={summary.averageRating} className="mt-1 text-lg" />
      </div>

      <div className="text-sm text-slate-500">
        <b className="text-slate-900">{summary.reviewCount.toLocaleString('en-US')} product reviews</b>
        <br />
        from verified subscribers
      </div>

      <div className="hidden self-stretch border-0 border-l border-solid border-slate-200 lg:block" />

      <p className="m-0 max-w-lg text-sm text-slate-600">
        Reviews of the magazines themselves — content, writing, value — from verified DiscountMags
        subscribers, collected after delivery.
      </p>
    </section>
  );
}
